/**
 * Liquid often sets `action` to `shop.url` + path (canonical storefront). On
 * `shopify theme dev` or custom preview hosts, `fetch()` would cross origins
 * and fail CORS. Rebuild the URL on `window.location.origin` while keeping
 * pathname + query (+ hash) from the template.
 *
 * @param {string | null | undefined} href
 * @returns {URL}
 */
function sameOriginUrlFromAction(href) {
  if (!href) {
    return new URL(window.location.href);
  }
  try {
    const parsed = new URL(href, window.location.href);
    return new URL(`${parsed.pathname}${parsed.search}${parsed.hash}`, window.location.origin);
  } catch {
    return new URL(window.location.href);
  }
}

class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}

customElements.define('facet-short', FacetShort, { extends: 'select' });
class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = sameOriginUrlFromAction(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    setTimeout(() => {
      const target = document.querySelector('.collection');
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 95,
        behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
      });
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
      ],
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  renderSection(url, event) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url, event) : this.renderSectionFromFetch(url, event);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event) {
    this.beforeRenderSection();
    const start = performance.now();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const execution = performance.now() - start;

        setTimeout(
          () => {
            this.renderFilters(responseText, event);
            this.renderFiltersActive(responseText);
            this.renderProductGridContainer(responseText);
            this.renderProductCount(responseText);
            this.renderSortBy(responseText);
            this.renderSortByMobile(responseText);

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
            this.cachedMap.set(url, responseText);

            this.afterRenderSection();
          },
          execution > 250 ? 0 : 250
        );
      });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    setTimeout(() => {
      const responseText = this.cachedMap.get(url);
      this.renderFilters(responseText, event);
      this.renderFiltersActive(responseText);
      this.renderProductGridContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);
      this.renderSortByMobile(responseText);

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

      this.afterRenderSection();
    }, 250);
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #FacetFiltersContainerDrawer [data-filter], #MobileFacetFiltersContainer [data-filter]'
    );

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    /** `data-index` can include characters that break `querySelector('[data-index="…"]')` — match in JS. */
    const liveFacetByIndex = new Map();
    document.querySelectorAll('[data-filter]').forEach((el) => {
      const key = el.dataset.index;
      if (key && !liveFacetByIndex.has(key)) liveFacetByIndex.set(key, el);
    });

    facetsToRender.forEach((element) => {
      const filter = liveFacetByIndex.get(element.dataset.index) ?? null;
      if (filter !== null) {
        if (filter.tagName === 'DETAILS') {
          filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
        } else {
          filter.innerHTML = element.innerHTML;
        }
      } else if (element.classList.contains('bm-facet-drawer__variant-shell')) {
        const liveWrap = document.querySelector('#FacetFiltersContainerDrawer .bm-facet-drawer__variants-wrap');
        if (liveWrap && element.closest('#FacetFiltersContainerDrawer')) {
          liveWrap.appendChild(document.importNode(element, true));
        }
      }
    });

    /**
     * Do **not** assign `liveVariantsWrap.innerHTML = parsedVariantsWrap.innerHTML`.
     * Section HTML from the parser can omit or empty the drawer variants strip; that wiped Bonded
     * Matter `bm-facet-drawer__variant-shell` rows and left only cloned vertical facets (plain
     * checkboxes, no stone icons). Per-`[data-filter]` updates above already refresh each shell.
     */
    if (typeof window.bmSyncFacetDrawerVariantsFromVertical === 'function') {
      window.bmSyncFacetDrawerVariantsFromVertical();
    }
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;

    const layoutSwitcher = document.querySelector('#ProductGridWrapper layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}

customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class LoadMoreButton extends HTMLButtonElement {
  static responseCache = new Map();
  static inFlightRequests = new Map();
  static maxPrefetchDepth = 5;

  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
    this.onViewportHandler = this.onViewportChange.bind(this);
    this.viewportRaf = 0;
    this.nativeObserver = null;
  }

  /**
   * @param {string} url
   * @returns {Promise<string>}
   */
  static fetchPage(url) {
    if (LoadMoreButton.responseCache.has(url)) {
      return Promise.resolve(LoadMoreButton.responseCache.get(url));
    }

    if (LoadMoreButton.inFlightRequests.has(url)) {
      return LoadMoreButton.inFlightRequests.get(url);
    }

    const request = fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Load more failed: ${response.status} ${response.statusText}`);
        }
        return response.text();
      })
      .then((responseText) => {
        LoadMoreButton.responseCache.set(url, responseText);
        return responseText;
      })
      .finally(() => {
        LoadMoreButton.inFlightRequests.delete(url);
      });

    LoadMoreButton.inFlightRequests.set(url, request);
    return request;
  }

  /**
   * @param {string | null} action
   * @param {string} sectionId
   * @returns {string | null}
   */
  static buildSectionUrlFromAction(action, sectionId) {
    if (!action || !sectionId) return null;
    const url = sameOriginUrlFromAction(action);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  /**
   * @param {string} responseText
   * @param {string} sectionId
   * @returns {string | null}
   */
  static extractNextPageUrl(responseText, sectionId) {
    const parsedRoot = LoadMoreButton.parseProductGridRoot(responseText);
    const nextButton = parsedRoot ? parsedRoot.querySelector('.pagination [is="load-more-button"]') : null;
    const action = nextButton ? nextButton.getAttribute('action') : null;
    return LoadMoreButton.buildSectionUrlFromAction(action, sectionId);
  }

  /**
   * @param {string | null} url
   * @param {string} sectionId
   * @param {number} depth
   * @returns {Promise<void>}
   */
  prefetchFromUrl(url, sectionId, depth) {
    if (!url || depth <= 0) return Promise.resolve();
    if (LoadMoreButton.responseCache.has(url)) {
      return Promise.resolve().then(() => {
        if (depth <= 1) return;
        const responseText = LoadMoreButton.responseCache.get(url);
        const nextUrl = LoadMoreButton.extractNextPageUrl(responseText, sectionId);
        return this.prefetchFromUrl(nextUrl, sectionId, depth - 1);
      });
    }

    return LoadMoreButton.fetchPage(url)
      .then((responseText) => {
        if (depth <= 1) return;
        const nextUrl = LoadMoreButton.extractNextPageUrl(responseText, sectionId);
        return this.prefetchFromUrl(nextUrl, sectionId, depth - 1);
      })
      .catch(() => {});
  }

  prefetchNextPage(depth = LoadMoreButton.maxPrefetchDepth) {
    const nextButton = document.querySelector('#ProductGridContainer .pagination [is="load-more-button"]');
    if (!nextButton) return;

    const sectionId = FoxTheme.utils.getSectionId(nextButton);
    if (!sectionId) return;

    const action = nextButton.getAttribute('action');
    const nextUrl = LoadMoreButton.buildSectionUrlFromAction(action, sectionId);
    if (!nextUrl) return;

    // Warm up multiple pages ahead so appends feel instant near list end.
    this.prefetchFromUrl(nextUrl, sectionId, depth);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      // Theme helper (Motion One `inView`) — kept as belt-and-braces.
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '3000px 0px 3000px 0px' });

      // Raw IntersectionObserver with a huge rootMargin so we start fetching
      // long before the sentinel scrolls into view, independent of any theme wrapper.
      if ('IntersectionObserver' in window) {
        this.nativeObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                this.onClickHandler();
                break;
              }
            }
          },
          { rootMargin: '4000px 0px 4000px 0px', threshold: 0 }
        );
        this.nativeObserver.observe(this);
      }

      window.addEventListener('scroll', this.onViewportHandler, { passive: true });
      window.addEventListener('resize', this.onViewportHandler, { passive: true });
    }

    this.prefetchNextPage();
    this.onViewportChange();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
    window.removeEventListener('scroll', this.onViewportHandler);
    window.removeEventListener('resize', this.onViewportHandler);
    if (this.nativeObserver) {
      this.nativeObserver.disconnect();
      this.nativeObserver = null;
    }
    if (this.viewportRaf) {
      window.cancelAnimationFrame(this.viewportRaf);
      this.viewportRaf = 0;
    }
  }

  /**
   * Section Rendering responses may be a full document or a `shopify-section-*` fragment; always
   * scope product grid queries under `#ProductGridContainer` so we never grab the wrong `.products-list`.
   *
   * @param {string} responseText
   * @returns {HTMLElement | null} The `#ProductGridContainer` element from the parsed response, or null.
   */
  static parseProductGridRoot(responseText) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    let root = parsedHTML.getElementById('ProductGridContainer');
    if (root) return root;
    const sectionRoot = parsedHTML.querySelector('[id^="shopify-section-"]');
    if (sectionRoot) {
      root = sectionRoot.querySelector('#ProductGridContainer');
      if (root) return root;
    }
    return null;
  }

  /**
   * Curated collections set `display: block` on `.bm-curated-grid__host`; grid columns must sit
   * inside `.bm-curated-grid__row--tail`, not as direct children of `grid-list` (otherwise each
   * card spans full width).
   *
   * @param {Element} productList
   * @returns {Element}
   */
  static resolveCuratedAppendParent(productList) {
    if (!productList.classList.contains('bm-curated-grid__host')) {
      return productList;
    }

    const existingTail = productList.querySelector('.bm-curated-grid__row--tail');
    if (existingTail) {
      return existingTail;
    }

    const curated = productList.querySelector('.bm-curated-grid');
    const classAttr = productList.getAttribute('data-bm-tail-row-classes');
    if (!curated || !classAttr) {
      return productList;
    }

    const row = document.createElement('div');
    row.className = classAttr;
    curated.appendChild(row);
    return row;
  }

  /**
   * @param {ParentNode} sourceList Parsed `.products-list` from the section response (page 2+ is a flat grid).
   * @returns {Element[]}
   */
  static collectDirectProductColumns(sourceList) {
    const out = [];
    for (const child of sourceList.children) {
      if (
        child.nodeType === Node.ELEMENT_NODE &&
        child.classList.contains('f-column') &&
        child.classList.contains('card')
      ) {
        out.push(child);
      }
    }
    return out;
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();

    LoadMoreButton.fetchPage(url)
      .then((responseText) => {
        const parsedRoot = LoadMoreButton.parseProductGridRoot(responseText);
        this.renderPagination(parsedRoot);
        this.renderProductGridContainer(parsedRoot);
        this.prefetchNextPage();
        this.onViewportChange();
      })
      .catch((err) => {
        console.error('[load-more-button]', err && err.message ? err.message : err);
      })
      .finally(() => {
        this.clearLoadingState();
      });
  }

  onViewportChange() {
    if (this.getAttribute('type') !== 'infinite') return;
    if (this.classList.contains('btn--loading')) return;
    if (this.viewportRaf) return;

    this.viewportRaf = window.requestAnimationFrame(() => {
      this.viewportRaf = 0;
      this.maybeAutoLoadAhead();
    });
  }

  maybeAutoLoadAhead() {
    if (!this.isConnected) return;
    if (this.getAttribute('type') !== 'infinite') return;
    if (this.classList.contains('btn--loading')) return;

    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    // Start appending before users hit the blank tail.
    const nearEnd = rect.top - viewportHeight < viewportHeight * 2.2;
    if (!nearEnd) return;

    const url = this.setUrl().toString();
    if (!url) return;

    if (LoadMoreButton.responseCache.has(url)) {
      this.onClick();
      return;
    }

    LoadMoreButton.fetchPage(url)
      .then(() => {
        if (!this.isConnected) return;
        const latestRect = this.getBoundingClientRect();
        const latestViewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const latestNearEnd = latestRect.top - latestViewportHeight < latestViewportHeight * 2.2;
        if (latestNearEnd && !this.classList.contains('btn--loading')) {
          this.onClick();
        }
      })
      .catch(() => {});
  }

  renderPagination(parsedRoot) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedRoot ? parsedRoot.querySelector('.pagination') : null;

    if (!pagination) return;

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(parsedRoot) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedRoot ? parsedRoot.querySelector('.products-list') : null;

    if (!source || !productList) return;

    const newColumns = LoadMoreButton.collectDirectProductColumns(source);
    if (!newColumns.length) return;

    const appendParent = LoadMoreButton.resolveCuratedAppendParent(productList);

    /*
     * Force incoming cards visible BEFORE insertion: the theme's `grid-list.showGridItems()`
     * otherwise resets every `.f-column` in the grid to `opacity:0.01; translateY(3.5rem)` and
     * staggers them back in over ~0.5s + 0.1s × N — with 15-30 cards that's 2+ seconds of
     * perceptual blank/flicker even though the HTML already arrived. We want appends to feel
     * like they "just appear", so skip the stagger entirely and inline the end state.
     *
     * Inline `style` wins over the theme's WAAPI animation if showGridItems is invoked elsewhere,
     * because `Motion.animate` writes to the element's style surface too; we set it explicitly
     * so there's no sub-frame flash.
     */
    const fragment = document.createDocumentFragment();
    newColumns.forEach((item) => {
      item.style.opacity = '1';
      item.style.visibility = 'visible';
      item.style.transform = 'none';
      fragment.appendChild(item);
    });
    appendParent.appendChild(fragment);
  }

  setUrl() {
    const url = sameOriginUrlFromAction(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }

  clearLoadingState() {
    this.classList.remove('pointer-events-none');
    this.classList.remove('btn--loading');
  }
}

customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'sleektheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  onButtonClick(event) {
    const target = event.target ? event.target : event;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);

        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;
    const removedClass = ['f-grid-1-cols', 'f-grid-2-cols'];
    removedClass.forEach((removed) => {
      productGrid.classList.remove(removed);
    });

    let addedClass = '';
    switch (layoutMode) {
      case 'grid-1':
        addedClass = 'f-grid-1-cols';
        break;
      case 'grid-2':
        addedClass = 'f-grid-2-cols';
        break;
    }
    productGrid.classList.add(addedClass);
    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });

    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}

customElements.define('layout-switcher', LayoutSwitcher);
